import type en from "../en/catalog";
import type { Messages } from "../../types";

const fr: Messages<typeof en> = {
  // Generic
  reset: "Réinitialiser",
  resetFilters: "Réinitialiser les filtres",
  hidden: "Masquée",
  rejected: "Refusés",
  reject: "Refuser",
  working: "En cours…",
  choose: "Choisir…",
  added: "Ajouté",
  draftParen: "(brouillon)",
  new: "Nouveau",
  newBreadcrumb: "Nouvelle",
  details: "Détails",
  applyEllipsis: "Appliquer…",
  viewOnStore: "Voir sur la boutique ↗",
  unsavedChanges: "Modifications non enregistrées",
  charCount: "{count} / {max} caractères",
  productCount: "{count} produits",
  productCount_one: "{count} produit",
  selectedCount: "{count} sélectionné(s)",
  exportCsv: "Exporter en CSV",
  importCsv: "Importer un CSV",
  import: "Importer",
  product: "Produit",
  collection: "Collection",
  sku: "SKU",
  barcode: "Code-barres",
  tag: "Étiquette",
  vendor: "Fournisseur",
  tags: "Étiquettes",
  image: "Image",
  noAltText: "Aucun texte alternatif",
  altText: "Texte alt. : {alt}",
  pageTitle: "Titre de la page",
  metaDescription: "Méta-description",
  urlHandle: "Identifiant d’URL",
  seoListing: "Aperçu dans les moteurs de recherche",
  visibility: "Visibilité",

  // Product types
  typePhysical: "Physique",
  typeDigital: "Numérique",
  typeService: "Service",
  typePhysicalLong: "Physique — expédié au client",
  typeDigitalLong: "Numérique — fichier à télécharger",
  typeServiceLong: "Service — sans expédition",

  // Stock states
  inStock: "En stock",
  lowStock: "Stock faible",
  outOfStock: "En rupture de stock",
  notTracked: "Non suivi",
  tracked: "Suivi",
  onHand: "En stock physique",
  reserved: "Réservé",
  available: "Disponible",

  // Products list
  productsDescription: "Tout ce que vous vendez — produits physiques, téléchargements numériques et services.",
  addProduct: "Ajouter un produit",
  productDeleted: "Produit supprimé.",
  productSearchPlaceholder: "Titre, fournisseur, SKU ou étiquette exacte",
  statusAllNotArchived: "Tous (hors archivés)",
  allTypes: "Tous les types",
  anyCollection: "Toutes les collections",
  stock: "Stock",
  anyStock: "Tous les niveaux de stock",
  sort: "Trier",
  sortNewest: "Plus récents",
  sortOldest: "Plus anciens",
  sortTitle: "Titre A–Z",
  sortPriceAsc: "Prix croissant",
  sortPriceDesc: "Prix décroissant",
  sortStock: "Stock : le plus faible d’abord",
  noProductsMatch: "Aucun produit ne correspond à ces filtres",
  noProductsMatchHint: "Essayez une autre recherche ou réinitialisez les filtres.",
  emptyProductsTitle: "Ajoutez votre premier produit",
  emptyProductsBody: "Les produits apparaissent dans votre boutique dès qu’ils sont actifs. Vous pouvez aussi importer un fichier CSV depuis une autre plateforme.",

  // Product table / bulk actions
  bulkSetActive: "Statut : Actif",
  bulkSetDraft: "Statut : Brouillon",
  bulkSetArchived: "Statut : Archivé",
  bulkPricePercent: "Ajuster les prix en %",
  bulkAddToCollection: "Ajouter à une collection",
  bulkRemoveFromCollection: "Retirer d’une collection",
  bulkAddTag: "Ajouter une étiquette",
  bulkSummaryIncrease: "Augmenter de {percent} % le prix de toutes les variantes de {products}. Les prix barrés qui ne seraient plus supérieurs au prix seront effacés.",
  bulkSummaryDecrease: "Baisser de {percent} % le prix de toutes les variantes de {products}. Les prix barrés qui ne seraient plus supérieurs au prix seront effacés.",
  bulkSummaryAddCollection: "Ajouter {products} à « {collection} ».",
  bulkSummaryRemoveCollection: "Retirer {products} de « {collection} ».",
  bulkSummaryAddTag: "Ajouter l’étiquette « {tag} » à {products}.",
  bulkSummaryArchive: "Archiver {products}. Les produits archivés sont masqués dans votre boutique mais conservés pour l’historique des commandes.",
  bulkSummaryActive: "Passer {products} au statut Actif.",
  bulkSummaryDraft: "Passer {products} au statut Brouillon.",
  bulkActions: "Actions groupées",
  bulkAction: "Action groupée",
  chooseAction: "Choisir une action…",
  percentChange: "Variation en pourcentage",
  percentPlaceholder: "ex. 10 ou -15",
  chooseCollection: "Choisir une collection…",
  confirmBulkUpdate: "Confirmer la mise à jour groupée",
  updating: "Mise à jour…",
  updateCount: "Mettre à jour {count}",
  selectAllOnPage: "Sélectionner tous les produits de cette page",
  selectProduct: "Sélectionner {title}",
  variantsCount: "{count} variantes",
  variantsCount_one: "{count} variante",
  singleVariant: "Variante unique",
  availableCount: "{count} disponibles",
  availableCount_one: "{count} disponible",
  lowShort: "faible",

  // Product actions (server)
  enterPrice: "Saisissez un prix",
  amountNegative: "Le montant ne peut pas être négatif",
  enterValidAmount: "Saisissez un montant valide",
  formUnreadable: "Le formulaire n’a pas pu être lu. Rechargez la page et réessayez.",
  checkPrices: "Vérifiez les prix signalés.",
  productSaved: "Produit enregistré.",
  productArchivedInstead: "Ce produit figure dans des commandes passées : il a donc été archivé au lieu d’être supprimé. L’historique des commandes conserve ses informations.",
  chooseActionError: "Choisissez une action",
  enterPercentChange: "Saisissez une variation en pourcentage, par ex. 10 ou -15.",
  enterPercentage: "Saisissez un pourcentage",
  chooseCollectionError: "Choisissez une collection.",
  chooseCollectionField: "Choisissez une collection",
  bulkUpdated: "{count} produits mis à jour.",
  bulkUpdated_one: "{count} produit mis à jour.",
  chooseCsvFile: "Choisissez un fichier CSV à importer.",
  chooseCsvField: "Choisissez un fichier CSV",
  csvTooLarge: "Les fichiers CSV ne doivent pas dépasser 5 Mo.",
  fileTooLarge: "Fichier trop volumineux",
  importErrorsDry: "{count} produits comportent des erreurs. Corrigez les lignes ci-dessous puis validez à nouveau.",
  importErrorsDry_one: "{count} produit comporte des erreurs. Corrigez les lignes ci-dessous puis validez à nouveau.",
  importErrorsReal: "{count} produits comportaient des erreurs et ont été ignorés. Corrigez les lignes ci-dessous puis importez à nouveau.",
  importErrorsReal_one: "{count} produit comportait des erreurs et a été ignoré. Corrigez les lignes ci-dessous puis importez à nouveau.",
  importValid: "Le fichier est valide : {created} produit(s) seraient créés et {updated} mis à jour. Rien n’a encore été enregistré.",
  importComplete: "Import terminé : {created} créé(s), {updated} mis à jour.",

  // Product pages
  editProduct: "Modifier le produit",
  productCreated: "Produit créé.",
  productDuplicated: "Dupliqué en brouillon. Les SKU et le stock n’ont pas été copiés — renseignez-les avant l’activation.",
  duplicate: "Dupliquer",
  duplicateTitle: "Dupliquer ce produit ?",
  duplicateBody: "Crée une copie en brouillon avec les mêmes informations, images et prix. Les SKU, codes-barres et stocks ne sont pas copiés.",
  deleteProductTitle: "Supprimer ce produit ?",
  deleteProduct: "Supprimer le produit",
  deleteProductBody: "Si ce produit figure dans une commande passée, il sera <b>archivé à la place</b> afin de préserver l’historique des commandes. Sinon, il est définitivement supprimé avec ses variantes et son historique de stock.",

  // Import
  importProducts: "Importer des produits",
  importPricesCurrency: "Les prix sont lus dans la devise de votre boutique ({currency}).",
  expectedColumns: "Colonnes attendues",
  template: "Modèle",
  exportCurrent: "Exporter l’existant",
  csvHeaderHelp: "La ligne d’en-tête doit contenir au minimum {handle} et {price}. L’ordre des colonnes n’a pas d’importance.",
  csvHelpHandle: "Obligatoire. Identifiant d’URL ; les lignes ayant le même identifiant sont des variantes d’un même produit.",
  csvHelpTitle: "Obligatoire sur la première ligne d’un produit.",
  csvHelpDescription: "Texte brut / Markdown simplifié.",
  csvHelpType: "physical, digital ou service (par défaut physical).",
  csvHelpStatus: "draft, active ou archived (par défaut draft).",
  csvHelpVendor: "Marque ou fabricant.",
  csvHelpTags: "Séparez les étiquettes par |",
  csvHelpOption1Name: "ex. Size (première ligne du produit).",
  csvHelpOption1Value: "ex. M (chaque ligne).",
  csvHelpSku: "Unique dans la boutique.",
  csvHelpPrice: "Obligatoire. Nombre décimal, ex. 24.00",
  csvHelpCompareAt: "Facultatif. Doit être supérieur au prix.",
  csvHelpCost: "Coût unitaire facultatif.",
  csvHelpStock: "Nombre entier ; définit le stock physique.",
  csvHelpTrackInventory: "true/false (par défaut true).",
  csvHelpWeight: "Nombre entier de grammes.",
  uploadCsv: "Importer un CSV",
  uploadCsvDescription: "Les produits sont associés par identifiant : les identifiants existants sont mis à jour, les nouveaux sont créés. Validez d’abord — rien n’est enregistré avant l’import.",
  csvFile: "Fichier CSV",
  csvFileHint: "CSV en UTF-8, jusqu’à 5 Mo et 5 000 lignes.",
  validating: "Validation…",
  importing: "Import…",
  validateOnly: "Valider uniquement",
  validationResults: "Résultats de la validation",
  importResults: "Résultats de l’import",
  dryRunNothingSaved: "simulation, rien n’a été enregistré",
  wouldCreate: "Seraient créés",
  created: "Créés",
  wouldUpdate: "Seraient mis à jour",
  updated: "Mis à jour",
  withErrors: "Avec erreurs",
  row: "Ligne",
  rowN: "Ligne {row}",
  problem: "Problème",

  // Product editor
  productNotSaved: "Produit non enregistré",
  productTitlePlaceholder: "ex. T-shirt en coton bio",
  productDescriptionHint: "Markdown simplifié : ligne vide entre les paragraphes, « ## » pour un titre, « - » pour les puces, **gras** et [texte du lien](https://…).",
  media: "Médias",
  mediaHint: "La première image est la photo principale du produit. Les images doivent provenir de la médiathèque publique.",
  mainImage: "Principale",
  moveImageEarlier: "Déplacer l’image {n} vers l’avant",
  moveImageLater: "Déplacer l’image {n} vers l’arrière",
  removeImageNamed: "Retirer l’image {name}",
  uploadImages: "Importer des images",
  altTextHint: "Le texte alternatif décrit l’image pour les lecteurs d’écran et les moteurs de recherche — modifiez-le dans la médiathèque.",
  digitalFile: "Fichier numérique",
  digitalFileHint: "Remis aux clients après paiement via des liens de téléchargement limités et temporaires. Stocké de façon privée — jamais accessible publiquement.",
  fileSizePrivate: "{size} Mo · privé",
  noFileAttached: "Aucun fichier joint pour l’instant. Les clients ne pourront pas recevoir ce produit tant que vous n’en aurez pas importé un.",
  replaceFile: "Remplacer le fichier",
  uploadFile: "Importer un fichier",
  downloadLimit: "Limite de téléchargements",
  downloadLimitHint: "Nombre de téléchargements autorisés par achat (1–100).",
  linkExpiry: "Expiration du lien (jours)",
  linkExpiryHint: "Nombre de jours de validité du lien de téléchargement (1–365).",
  options: "Options",
  optionsHint: "Jusqu’à 3 options, comme Taille ou Couleur. Une variante est générée pour chaque combinaison.",
  optionName: "Nom de l’option {n}",
  optionNamePlaceholder: "ex. Taille",
  values: "Valeurs",
  valuesHint: "Séparez les valeurs avec Entrée ou une virgule.",
  valuesPlaceholder: "ex. S, M, L",
  optionNamesUnique: "Les noms d’options doivent être uniques.",
  addOption: "+ Ajouter une option",
  variantsMaxWarning: "{count} variantes — le maximum est de 250.",
  pricing: "Tarification",
  compareAtPrice: "Prix barré",
  compareAtHint: "Prix d’origine affiché barré.",
  costPerItem: "Coût unitaire",
  barcodeFull: "Code-barres (ISBN, UPC, GTIN…)",
  digitalNotTracked: "Le stock des produits numériques n’est pas suivi.",
  servicesNotTracked: "Le stock des services n’est pas suivi.",
  variantsTitle: "Variantes ({count})",
  variantsHint: "Les variantes existantes conservent leurs prix, stock et historique lorsque vous modifiez les options.",
  priceForAll: "Prix pour toutes les variantes",
  applyToAll: "Appliquer à toutes",
  variant: "Variante",
  defaultVariant: "Par défaut",
  priceFor: "Prix de {label}",
  skuFor: "SKU de {label}",
  stockFor: "Stock physique de {label}",
  variantImage: "Image de la variante",
  addImagesFirst: "Ajoutez d’abord des images au produit.",
  noSpecificImage: "Aucune image spécifique",
  imageOption: "Image {n} — {name}",
  requiresShipping: "Il s’agit d’un produit physique qui doit être expédié",
  weightGrams: "Poids (grammes)",
  weightHint: "Utilisé pour les tarifs d’expédition basés sur le poids. Votre boutique affiche les poids en {unit} ; saisissez ici des grammes (1 kg = 1000 g, 1 lb ≈ 454 g).",
  weightPerVariantHint: "Définissez le poids de chaque variante (en grammes) dans Détails.",
  noShippingDigital: "Les produits numériques sont livrés par téléchargement — aucun frais d’expédition n’est facturé.",
  seoProductHint: "Apparence de ce produit dans les résultats de recherche.",
  seoTitlePlaceholder: "Titre du produit",
  seoDescriptionPlaceholder: "Ajoutez une description pour afficher un résumé ici.",
  slugHint: "Lettres minuscules, chiffres et tirets. Laissez vide pour le générer à partir du titre.",
  slugHintExisting: "Lettres minuscules, chiffres et tirets. Laissez vide pour le générer à partir du titre. Le modifier rend les liens existants invalides.",
  statusDraftLong: "Brouillon — masqué dans la boutique",
  statusActiveLong: "Actif — visible et disponible à l’achat",
  statusArchivedLong: "Archivé — masqué, conservé pour l’historique",
  organization: "Organisation",
  productType: "Type de produit",
  tagsHint: "Utilisées pour la recherche, les filtres et les collections automatiques.",
  noManualCollections: "Aucune collection manuelle pour l’instant.",
  createOne: "En créer une",
  automaticCollectionsHint: "Les collections automatiques ajoutent ce produit lorsqu’il correspond à leurs règles.",
  tax: "Taxes",
  chargeTax: "Appliquer les taxes sur ce produit",
  taxClass: "Catégorie de taxe",
  taxClassHint: "Facultatif. Correspond aux taux de taxe de la même catégorie (ex. reduced). Laissez vide pour le taux normal.",
  createProduct: "Créer le produit",
  trackInventory: "Suivre le stock",
  trackInventoryHint: "Le stock diminue à chaque commande.",
  continueSelling: "Continuer à vendre en cas de rupture",
  continueSellingHint: "Autorise les précommandes.",
  stockOnHand: "Stock physique",
  stockReservedHint: "{count} réservé(s) par des paiements en cours. Les modifications sont journalisées.",
  stockLoggedHint: "Les modifications sont consignées dans l’historique du stock.",
  initialStockHint: "Enregistré comme stock initial.",
  lowStockAlertAt: "Alerte de stock faible à",
  lowStockAlertHint: "Signaler lorsque le stock disponible atteint ce seuil.",
  weightStoreHint: "La boutique affiche en {unit} ; saisissez des grammes.",
  customersWontSee: "Les clients ne verront pas cette information.",
  marginHint: "Marge {margin} % · les clients ne verront pas cette information.",

  // Pickers / inputs
  typeAndEnter: "Saisissez puis appuyez sur Entrée",
  removeTag: "Retirer {tag}",
  searchProducts: "Rechercher des produits",
  searchProductsPlaceholder: "Rechercher des produits par titre ou SKU",
  noProductsSelected: "Aucun produit sélectionné.",
  moveUp: "Monter {title}",
  moveDown: "Descendre {title}",
  removeItem: "Retirer {title}",
  chooseFromLibrary: "Choisir dans la médiathèque",
  searchFiles: "Rechercher des fichiers",
  searchByFileName: "Rechercher par nom de fichier",
  uploadNew: "Importer",
  mediaLoadError: "Impossible de charger la médiathèque.",
  noImagesMatch: "Aucune image ne correspond à votre recherche.",
  libraryEmpty: "Votre médiathèque est vide. Importez une image pour commencer.",
  loadMore: "Charger plus",
  selectImages: "Sélectionnez une ou plusieurs images",
  selectImage: "Sélectionnez une image",
  addSelected: "Ajouter la sélection",
  useImage: "Utiliser l’image",

  // Collections
  collectionsDescription: "Regroupez des produits pour la navigation, les pages d’atterrissage et les réductions.",
  createCollection: "Créer une collection",
  editCollection: "Modifier la collection",
  collectionDeleted: "Collection supprimée. Ses produits n’ont pas été affectés.",
  collectionCreated: "Collection créée.",
  collectionSaved: "Collection enregistrée.",
  noCollections: "Aucune collection pour l’instant",
  noCollectionsBody: "Créez une collection comme « Nouveautés » ou « Moins de 25 € » pour aider vos clients à parcourir la boutique.",
  manual: "Manuelle",
  automatic: "Automatique",
  ruleSummaryTag: "étiquette = {value}",
  ruleSummaryType: "type = {value}",
  ruleSummaryVendor: "fournisseur = {value}",
  ruleSummaryPriceLte: "prix ≤ {amount}",
  ruleSummaryPriceGte: "prix ≥ {amount}",
  deleteCollectionTitle: "Supprimer cette collection ?",
  deleteCollection: "Supprimer la collection",
  deleteCollectionBody: "La collection est retirée de votre boutique. Les produits qu’elle contient ne sont pas supprimés. Les réductions limitées à cette collection ne s’y appliqueront plus.",
  enterValue: "Saisissez une valeur",
  chooseProductType: "Choisissez un type de produit",
  checkRules: "Vérifiez les règles de la collection.",
  ruleTag: "L’étiquette du produit est",
  ruleType: "Le type de produit est",
  ruleVendor: "Le fournisseur est",
  rulePriceLte: "Le prix est au maximum de",
  rulePriceGte: "Le prix est au minimum de",
  collectionTitlePlaceholder: "ex. Soldes d’été",
  collectionDescriptionHint: "Affichée en haut de la page de la collection. Markdown simplifié pris en charge.",
  collectionType: "Type de collection",
  collectionTypeHint: "Les collections manuelles contiennent les produits que vous choisissez. Les collections automatiques incluent tous les produits qui respectent toutes les règles.",
  manualHint: "Choisissez et ordonnez vous-même les produits.",
  automaticHint: "Les produits sont ajoutés selon l’étiquette, le type, le fournisseur ou le prix.",
  switchToAutomaticWarning: "Passer en mode automatique retire de cette collection les produits sélectionnés manuellement.",
  matchAllRules: "Les produits doivent respecter toutes ces règles",
  ruleCondition: "Condition de la règle {n}",
  ruleValue: "Valeur de la règle {n}",
  tagPlaceholder: "ex. été",
  vendorPlaceholder: "ex. Acme",
  addRule: "+ Ajouter une règle",
  currentlyMatching: "Correspondances actuelles (règles enregistrées)",
  noProductsMatchYet: "Aucun produit ne correspond pour l’instant.",
  collectionNoProducts: "Aucun produit pour l’instant — utilisez la recherche ci-dessus pour en ajouter.",
  manualOrderHint: "Cet ordre est utilisé lorsque la collection est triée manuellement.",
  urlPreview: "Aperçu : {url}",
  publishedHint: "Visible dans la navigation et les pages de collection de votre boutique.",
  sortProductsBy: "Trier les produits par",
  sortManual: "Ordre manuel",
  sortNewestFirst: "Plus récents d’abord",
  removeImage: "Retirer l’image",
  noImage: "Aucune image.",
  uploadImage: "Importer une image",

  // Inventory
  inventoryDescription: "Niveaux de stock de chaque variante. Disponible = stock physique − réservé (bloqué pour les commandes non payées et les paiements en cours).",
  trackedVariants: "Variantes suivies",
  unitsOnHand: "Unités en stock",
  lowStockHint: "Au seuil ou en dessous",
  inventorySearchPlaceholder: "Produit, variante, SKU ou code-barres",
  allVariants: "Toutes les variantes",
  lowInclOut: "Faible (ruptures incluses)",
  nothingMatches: "Aucun résultat",
  nothingMatchesHint: "Essayez une autre recherche ou un autre filtre.",
  noInventory: "Aucun stock pour l’instant",
  noInventoryBody: "Ajoutez un produit pour commencer à suivre le stock.",
  threshold: "Seuil",
  backordersAllowed: "Précommandes autorisées",
  history: "Historique",
  stockHistory: "Historique du stock",
  stockHistoryDescription: "Historique des mouvements de stock (du plus récent au plus ancien).",
  currentStock: "Stock actuel",
  tracking: "Suivi",
  lowStockAt: "Stock faible à",
  movements: "Mouvements",
  movementsHint: "Les réservations bloquent du stock pour les commandes non payées ; elles ne modifient pas le stock physique.",
  noMovements: "Aucun mouvement pour l’instant",
  noMovementsBody: "Les ajustements, ventes, retours et réservations apparaîtront ici.",
  event: "Événement",
  change: "Variation",
  onHandAfter: "Stock après",
  reasonReference: "Motif / référence",
  by: "Par",
  reservedCount: "{count} réservé(s)",
  viewOrder: "Voir la commande",
  system: "Système",
  kindAdjustment: "Ajustement",
  kindReceipt: "Réception",
  kindOrder: "Vente",
  kindCancellation: "Commande annulée",
  kindReturn: "Remis en stock (remboursement/retour)",
  kindReservation: "Réservation",
  kindReservationRelease: "Réservation libérée",
  kindImport: "Import",
  noStockChange: "Aucun changement — le stock est déjà à ce niveau.",
  stockChanged: "Stock physique : {before} → {after}",

  // Adjust stock
  adjust: "Ajuster",
  adjustStockTitle: "Ajuster le stock — {label}",
  saveAdjustment: "Enregistrer l’ajustement",
  adjustReservedNote: "{count} réservé(s) par des commandes non payées ou des paiements en cours",
  adjustLogged: "Chaque ajustement est enregistré avec votre nom et le motif.",
  adjustmentType: "Type d’ajustement",
  addRemove: "Ajouter / retirer",
  setTo: "Définir à",
  changeLabel: "Variation (utilisez − pour retirer)",
  newOnHand: "Nouvelle quantité en stock",
  onHandAfterValue: "Stock après : {count}",
  onHandAfterNegative: "Stock après : {count} — ne peut pas être inférieur à 0",
  deltaPlaceholder: "ex. 12 ou -2",
  setPlaceholder: "ex. 40",
  reason: "Motif",
  reasonReceived: "Réception de stock",
  reasonDamaged: "Endommagé",
  reasonCorrection: "Correction",
  reasonReturned: "Retourné",
  reasonOther: "Autre",
  note: "Note",
  noteRequiredOther: "Obligatoire pour « Autre ».",
  noteHint: "ex. numéro de bon de commande ou fournisseur.",

  // Reviews
  reviewsDescription: "Les nouveaux avis attendent ici jusqu’à ce que vous les publiiez. La mention « Achat vérifié » est attribuée automatiquement à partir des commandes payées — les auteurs ne peuvent pas la revendiquer.",
  reviewsNotInPlan: "Les avis produits ne sont pas inclus dans votre forfait actuel : les clients ne peuvent donc pas en publier de nouveaux. Les avis existants peuvent toujours être modérés.",
  noReviewsWaiting: "Aucun avis en attente",
  noReviewsHere: "Aucun avis ici",
  reviewsCaughtUp: "Vous êtes à jour. Les nouveaux avis clients apparaîtront ici pour approbation.",
  reviewsModeratedHere: "Les avis que vous modérez apparaîtront dans cette liste.",
  starsAria: "{rating} étoiles sur 5",
  verifiedPurchase: "Achat vérifié",
  reportedTimes: "Signalé {count} fois",
  reviewOn: "sur",
  viewOnStoreLower: "voir sur la boutique ↗",
  deleteReviewTitle: "Supprimer cet avis ?",
  deleteReviewBody: "L’avis est définitivement supprimé. Le refuser le garde masqué à la place.",
  reviewDeleted: "Supprimé.",
  reviewPublished: "Publié.",
  reviewRejected: "Refusé.",
};

export default fr;
